import React from 'react';
import { LanguagesTypes } from '@hydra/types/index';

import {
  Typography,
  Container,
  ButtonBase,
  IconButton
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { Icon, Translate, LanguageSwitcher } from '@hydra/common/components';
import { useErrorContext, useUserContext } from '@hydra/common/context';

import { usePricingRoutes, useI18n } from '@hydra/common/customHooks';

import getTranslatedUrl from '@hydra/utils/getTranslatedUrl';
import { getIsDudaUser } from '@hydra/utils/userUtil';
import getHelpLink from '@hydra/utils/getHelpLink';
import getAboutLink from '@hydra/utils/getAboutLink';
import isInIframe from '@hydra/utils/isInIframe';

const useStyles = makeStyles(theme => ({
  itemColumn: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: 0,
    alignItems: 'center',
    minWidth: 100,
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(1),
      alignItems: 'flex-start',
      minWidth: 150
    }
  },
  itemTitle: {
    pointerEvents: 'none',
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(1.5)
  },
  itemLink: {
    maxWidth: 100,
    fontSize: theme.typography.fontSizeSmall,
    marginBottom: theme.spacing(1),
    wordBreak: 'break-word',
    '&:hover': {
      textDecoration: 'none',
      color: theme.palette.primary.main
    }
  },
  smallFooterRoot: {
    display: 'flex',
    flexShrink: 0,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 0)
  },
  smallFooterContainer: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'flex-end'
    }
  },
  smallFooterItem: {
    padding: theme.spacing(1),
    fontSize: theme.typography.fontSizeSmall,
    fontWeight: theme.typography.fontWeightSemibold
  },
  largeFooterContainer: {
    marginTop: theme.spacing(8)
  },
  largeFooterHeader: {
    display: 'flex',
    padding: theme.spacing(2, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between'
    }
  },
  footerItems: {
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'center'
    }
  },
  socialItems: {
    display: 'flex'
  },
  largeFooterFooter: {
    display: 'flex',
    padding: theme.spacing(2, 0),
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTop: `1px solid ${theme.palette.divider}`
  },
  largeFooterBranding: {
    marginRight: theme.spacing(4),
    maxWidth: '200px'
  },
  branding: {
    fill: theme.palette.common.black,
    height: '40px !important',
    width: '140px !important'
  }
}));

export const FooterItems = ({ lang = 'en' }: { lang: LanguagesTypes }) => {
  const classes = useStyles();
  const { getPricingPageRoute } = usePricingRoutes();
  const items = [
    {
      title: 'footer_menu_title_resources',
      items: [
        {
          label: 'blog',
          href: `https://www.woorank.com/${lang}/blog`
        },
        {
          label: 'layout_seo_guides',
          href: `https://www.woorank.com/${lang}/edu/seo-guides`
        },
        {
          label: 'footer_menu_title_videos',
          href: 'https://www.youtube.com/user/WooRankTV'
        },
        {
          label: 'footer_menu_title_customerstories',
          href: 'https://www.woorank.com/en/customer-stories'
        },
        {
          label: 'footer_menu_title_release_notes',
          href: `https://www.woorank.com/en/release-notes/${new Date().getFullYear().toString()}`
        }
      ]
    },
    {
      title: 'footer_menu_title_woorank',
      items: [
        {
          label: 'pages_whywoorank_header',
          href: getTranslatedUrl('why-woorank', lang)
        },
        {
          label: 'pricing',
          href: getPricingPageRoute()
        },
        {
          label: 'footer_menu_title_experts',
          href: getTranslatedUrl('experts', lang)
        },
        {
          label: 'woo_index',
          href: 'https://index.woorank.com/en/reviews'
        },
        {
          label: 'layout_extension',
          href: getTranslatedUrl('extension', lang)
        }
      ]
    },
    {
      title: 'footer_menu_title_company',
      items: [
        {
          label: 'about',
          href: getAboutLink(lang)
        },
        {
          label: 'footer_menu_title_jobs',
          href: 'https://www.bridgeline.com/about/careers'
        },
        {
          label: 'privacy',
          href: `https://www.woorank.com/${lang}/p/privacy`
        },
        {
          label: 'cookies',
          href: `https://www.woorank.com/${lang}/p/privacy#cookie_policy`
        },
        {
          label: 'terms_of_service',
          href: `https://www.woorank.com/${lang}/p/tos`
        }
      ]
    },
    {
      title: 'footer_menu_title_support',
      items: [
        {
          label: 'faq_contact_us',
          href: getHelpLink(lang)
        }
      ]
    }
  ];

  return (
    <>
      {items.map((column, index) =>
        <div
          key={index}
          id={`footer_menu_${index}`}
          className={classes.itemColumn}
        >
          <div className={classes.itemTitle}>
            <Translate content={column.title} />
          </div>
          {
            column.items.map((item, index) =>
              item.href && (
                <ButtonBase
                  key={index}
                  className={classes.itemLink}
                  target='_blank'
                  rel='nofollow noopener noreferrer'
                  href={item.href}
                >
                  <Translate content={item.label} />
                </ButtonBase>
              )
            )
          }
        </div>
      )}
    </>
  );
};

export const SocialItems = () => {
  const items = [
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/woorank/',
      icon: 'facebook-nocolor'
    },
    {
      label: 'Twitter',
      href: 'https://twitter.com/woorank?ref_src=twsrc%5Egoogle%7Ctwcamp%5Eserp%7Ctwgr%5Eauthor',
      icon: 'twitter-nocolor'
    },
    {
      label: 'LinkedIn',
      href: 'https://www.linkedin.com/company/woorank',
      icon: 'linkedin-nocolor'
    },
    {
      label: 'YouTube',
      href: 'https://www.youtube.com/user/WooRankTV',
      icon: 'youtube-nocolor'
    },
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/woorank/?hl=en',
      icon: 'instagram-nocolor'
    }
  ];

  return (
    <>
      {items.map((item, index) =>
        <IconButton
          key={index}
          href={item.href}
          target='_blank'
        >
          <Icon type={item.icon} size='md' color='grey' />
        </IconButton>
      )}
    </>
  );
};

export function LargeFooter ({ lang = 'en' }: { lang: LanguagesTypes }) {
  const classes = useStyles();
  return (
    <>
      <Container>
        <div className={classes.largeFooterContainer}>
          <div className={classes.largeFooterHeader}>
            <div className={classes.footerItems}>
              <FooterItems lang={lang} />
            </div>
            <div className={classes.socialItems}>
              <SocialItems />
            </div>
          </div>
          <div className={classes.largeFooterFooter}>
            <ButtonBase href={`/${lang}/`} className={classes.largeFooterBranding}>
              <Icon className={classes.branding} type='woorank-by-bridgeline' viewBox='0 0 140 40' />
            </ButtonBase>
            <LanguageSwitcher />
          </div>
        </div>
      </Container>
    </>
  );
}

export function SmallFooter ({ lang = 'en' }: { lang: LanguagesTypes }) {
  const { user } = useUserContext();
  const isDudaUser = getIsDudaUser(user);
  const inIframe = isInIframe() === null ? true : isInIframe();

  const classes = useStyles();
  return (
    <div className={classes.smallFooterRoot}>
      <div className={classes.smallFooterContainer}>
        <ButtonBase href={getHelpLink(lang)} target='_blank' rel='nofollow noopener noreferrer'>
          <Typography className={classes.smallFooterItem} noWrap>
            <Translate content='faq_contact_us' />
          </Typography>
        </ButtonBase>
        <ButtonBase href={`https://www.woorank.com/${lang}/p/privacy`} target='_blank' rel='nofollow noopener noreferrer'>
          <Typography className={classes.smallFooterItem} noWrap>
            <Translate content='privacy' />
          </Typography>
        </ButtonBase>
        <ButtonBase href={`https://www.woorank.com/${lang}/p/tos`} target='_blank' rel='nofollow noopener noreferrer'>
          <Typography className={classes.smallFooterItem} noWrap>
            <Translate content='terms_of_service' />
          </Typography>
        </ButtonBase>
        {
          isDudaUser && inIframe
            ? null
            : (
              <div className={classes.smallFooterItem}>
                <LanguageSwitcher />
              </div>
              )
        }
      </div>
    </div>
  );
}

interface IFooterProps {
  small?: boolean
  bgColor?: string
}

export function Footer ({ small = true, bgColor: bgColorProp }: IFooterProps) {
  const theme = useTheme();
  const { locale } = useI18n();
  const { error } = useErrorContext();
  const bgColor = bgColorProp || theme.palette.background.default;

  return (
    <footer
      id='footer'
      style={{ backgroundColor: !error ? bgColor : theme.palette.background.default }}
    >
      {
        small
          ? <SmallFooter lang={locale} />
          : <LargeFooter lang={locale} />
      }
    </footer>
  );
}

export default React.memo(Footer);
